import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { Node, Row, ContractListItem, UpdateNodeRequest } from '@getaccept/editor-lib';
import { EditorHelper, getImageURLQuery } from '@getaccept/editor-lib';
import type { LoadProductsPayload } from '@getaccept/lib-shared/src/product-library/types/load-products-payload';
import type { Product } from '@getaccept/lib-shared/src/product-library/types/product';
import { capitalize } from '@getaccept/lib-shared/src/helpers';
import type { ContentLinkBlock } from '@getaccept/lib-shared/src/types/content-link';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type {
  CreateImage,
  StoredImage,
  ImageSearchPayload,
  ImageSearchResult,
  ContractFilter,
} from '../../types';
import {
  createRowMutation,
  moveCellMutation,
  moveRowMutation,
  updateCellsMutation,
  updateNodeMutation,
  deleteRowMutation,
  splitRowMutation,
  updateRowMutation,
  createSectionMutation,
  deleteSectionMutation,
  moveSectionMutation,
  updateSectionMutation,
  updateContentMutation,
  createImageV2Mutation,
} from '../graphql/editor-block.mutations';
import {
  convertToImagesPostLinkQuery,
  imageLibrarySearchQuery,
  trackImageLibraryDownloadQuery,
  getDocumentsQuery,
  getDocumentQuery,
  getSuggestedDocumentsQuery,
  getAllProductsQuery,
  documentEditorContentVersionsQuery,
  previewLinkQuery,
} from '../graphql/editor-block.queries';
import { ContentRequestType } from '../../store/editor-ui.types';
import type {
  SplitRowRequest,
  MoveCellRequest,
  CreateRowRequest,
  UpdateCellsRequest,
  MoveRowRequest,
  DeleteRowRequest,
  ContentRequest,
  UpdateRowRequest,
  CreateSectionRequest,
  DeleteSectionRequest,
  MoveSectionRequest,
  UpdateSectionRequest,
} from './types/requests';
import type { EditorContentVersionMapping } from './types/editor-content-version-mapping';

export class EditorBlockService {
  static stripUnwantedProperties(value: any) {
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index++) {
        value[index] = this.stripUnwantedProperties(value[index]);
      }
      return value;
    }

    if (typeof value !== 'object') {
      return value;
    }

    for (const property in value) {
      if (typeof value[property] === 'object') {
        delete value.property;
        const newData = this.stripUnwantedProperties(value[property]);
        value[property] = newData;
      } else {
        // updating column count fails if srcUrl is not undefined everywhere
        value['srcUrl'] = undefined;
        if (
          [
            '__typename',
            '_tempSelectionId',
            '_tempSlideShowId',
            '_tempSlideShowName',
            'blurhash',
          ].includes(property)
        ) {
          delete value[property];
        }
      }
    }

    return value;
  }

  static async createRow(payload: CreateRowRequest): Promise<Row> {
    const {
      data: { newRow },
    } = await apolloClientInternal.mutate({
      mutation: createRowMutation,
      variables: this.stripUnwantedProperties(structuredClone(payload)),
    });
    return newRow;
  }

  static async createSection(payload: CreateSectionRequest): Promise<boolean> {
    const variables = this.stripUnwantedProperties(structuredClone(payload));
    const result = await apolloClientInternal.mutate({
      mutation: createSectionMutation,
      variables,
    });
    return !!result;
  }

  static async moveCell(payload: MoveCellRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: moveCellMutation,
      variables: payload,
    });
    return !!result;
  }

  static async moveSection(payload: MoveSectionRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: moveSectionMutation,
      variables: payload,
    });
    return !!result;
  }

  static async moveRow(payload: MoveRowRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: moveRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateCells(payload: UpdateCellsRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: updateCellsMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateNode(payload: UpdateNodeRequest): Promise<Node> {
    const patchedPayload = EditorHelper.updateNodeFieldsToString(payload);
    const {
      data: { updatedNode },
    } = await apolloClientInternal.mutate({
      mutation: updateNodeMutation,
      variables: this.stripUnwantedProperties(structuredClone(patchedPayload)),
    });
    return updatedNode;
  }

  static async splitRow(payload: SplitRowRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: splitRowMutation,
      variables: this.stripUnwantedProperties(structuredClone(payload)),
    });
    return !!result;
  }

  static async deleteRow(payload: DeleteRowRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: deleteRowMutation,
      variables: payload,
    });
    return !!result;
  }

  static async deleteSection(payload: DeleteSectionRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: deleteSectionMutation,
      variables: payload,
    });
    return !!result;
  }

  static async updateRow(payload: UpdateRowRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: updateRowMutation,
      variables: this.stripUnwantedProperties(structuredClone(payload)),
    });
    return !!result;
  }

  static async updateSection(payload: UpdateSectionRequest): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: updateSectionMutation,
      variables: payload,
    });
    return !!result;
  }

  static async createImage({
    name,
    url,
    type,
    file,
    fileInfo,
    externalId,
  }: CreateImage): Promise<StoredImage> {
    const {
      data: {
        createImageV2: { image, uploadLink },
      },
    } = await apolloClientInternal.mutate({
      mutation: createImageV2Mutation,
      variables: {
        name,
        url,
        type,
        fileInfo,
        externalId,
      },
    });

    if (uploadLink) {
      const body = new FormData();
      uploadLink.headers
        .split(',')
        .map(header => header.split(':'))
        .forEach(([key, value]) => body.append(key, value));
      body.append('Content-Type', file.type);
      body.append('file', file);

      await fetch(uploadLink.url, {
        method: 'POST',
        body,
      });
    }

    return image;
  }

  static async dispatchRequest(payload: ContentRequest, type: ContentRequestType) {
    switch (type) {
      case ContentRequestType.UpdateCells: {
        await this.updateCells(payload as UpdateCellsRequest);
        break;
      }
      case ContentRequestType.CreateRow: {
        await this.createRow(payload as CreateRowRequest);
        break;
      }
      case ContentRequestType.SplitTextCreateRow: {
        await this.splitRow(payload as SplitRowRequest);
        break;
      }
      case ContentRequestType.DeleteRow: {
        await this.deleteRow(payload as DeleteRowRequest);
        break;
      }
      case ContentRequestType.MoveSection: {
        await this.moveSection(payload as MoveSectionRequest);
        break;
      }
      case ContentRequestType.MoveRow: {
        await this.moveRow(payload as MoveRowRequest);
        break;
      }
      case ContentRequestType.MoveCell: {
        await this.moveCell(payload as MoveCellRequest);
        break;
      }
      case ContentRequestType.UpdateNode: {
        await this.updateNode(payload as UpdateNodeRequest);
        break;
      }
      case ContentRequestType.UpdateRow: {
        await this.updateRow(payload as UpdateRowRequest);
        break;
      }
      case ContentRequestType.UpdateSection: {
        await this.updateSection(payload as UpdateSectionRequest);
        break;
      }
      case ContentRequestType.UpdateContentFontSettings:
      case ContentRequestType.UpdateContent: {
        await this.updateContent(payload as any);
        break;
      }
      case ContentRequestType.CreateSection: {
        await this.createSection(payload as CreateSectionRequest);
        break;
      }
      case ContentRequestType.DeleteSection: {
        await this.deleteSection(payload as DeleteSectionRequest);
        break;
      }

      default:
        break;
    }
  }

  static async imageLibrarySearch({
    search,
    page,
    perPage,
  }: ImageSearchPayload): Promise<ImageSearchResult> {
    const {
      data: { imageLibrarySearch },
    } = await apolloClientInternal.query({
      query: imageLibrarySearchQuery,
      variables: {
        search,
        page,
        perPage,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return imageLibrarySearch;
  }

  static async trackImageLibraryDownload(imageId: string): Promise<boolean> {
    const result = await apolloClientInternal.query({
      query: trackImageLibraryDownloadQuery,
      variables: {
        imageId,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return !!result;
  }

  static async updateContent({ targetContentIds, content }): Promise<boolean> {
    const result = await apolloClientInternal.mutate({
      mutation: updateContentMutation,
      variables: {
        targetContentIds,
        payload: this.stripUnwantedProperties(structuredClone(content)),
      },
    });
    return !!result.data.updateEditorContent;
  }

  static async convertToImagesPostLink(filename: string): Promise<any> {
    const {
      data: { convertToImagesPostLink },
    } = await apolloClientInternal.query({
      query: convertToImagesPostLinkQuery,
      variables: {
        filename,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return convertToImagesPostLink;
  }

  static async uploadFiles(
    file: File,
    uploadLink: string,
    headers: Record<string, string>
  ): Promise<void> {
    const body = new FormData();
    Object.keys(headers).forEach((key: string) => body.append(key, headers[key]));
    body.append('file', file);

    return new Promise((res, rej) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadLink, true);
      xhr.send(body);
      xhr.onload = function () {
        this.status === 204 ? res() : rej(new Error(this.responseText));
      };
    });
  }

  static async getImageUrl(
    id: string,
    height?: string
  ): Promise<{ url: string; externalId: string; blurhash: string }> {
    const {
      data: { image },
    } = await apolloClientInternal.query({
      query: getImageURLQuery,
      variables: { id, height },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return {
      url: image?.url || '',
      externalId: image?.externalId || undefined,
      blurhash: image?.blurhash || '',
    };
  }

  static async loadDocuments({
    documentFilter,
    userIds,
    orderBy,
    limit,
    pageNumber,
    tagIds,
    withoutTags,
    nameSearch,
  }: ContractFilter): Promise<ContractListItem[]> {
    const excludeTags: boolean = !tagIds?.length || withoutTags;
    const {
      data: { getDocuments },
    } = await apolloClientInternal.query({
      query: getDocumentsQuery,
      variables: {
        documentFilter: capitalize(documentFilter),
        userIds,
        orderBy: orderBy?.sortBy ? `${orderBy.sortBy}:${orderBy.sortOrder}` : undefined,
        limit,
        offset: pageNumber * limit,
        tagIds: excludeTags ? undefined : tagIds,
        withoutTags,
        nameSearch,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return getDocuments;
  }

  static async loadDocument(documentId: string): Promise<ContractListItem> {
    const {
      data: { document },
    } = await apolloClientInternal.query({
      query: getDocumentQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return document;
  }

  static async loadSuggestedContracts(contactIds: string[]): Promise<ContractListItem[]> {
    const {
      data: { suggestedContracts },
    } = await apolloClientInternal.query({
      query: getSuggestedDocumentsQuery,
      variables: { contactIds },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });

    return suggestedContracts;
  }

  static async loadProducts({
    searchTerms,
    limit,
    offset,
    sort,
    sortBy,
  }: LoadProductsPayload): Promise<{ products: Product[]; totalCount: number }> {
    const {
      data: {
        getAllProducts: { products, totalCount },
      },
    } = await apolloClientInternal.query({
      query: getAllProductsQuery,
      variables: {
        searchTerms,
        limit,
        offset: offset * limit,
        sort,
        sortBy,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return { products, totalCount };
  }

  static async exportDriveFileToPdf(id: string, type: string): Promise<File> {
    const baseUrl = `https://www.googleapis.com/drive/v3/files/${id}`;
    const typeUrl = type === 'file' ? '?alt=media' : '/export?mimeType=application/pdf';
    const url = `${baseUrl}${typeUrl}&supportsAllDrives=true&key=AIzaSyAxkddQyDkQH8Gc7yU0KUD9n4GRhq3Rat8`;

    return new Promise((res, rej) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      xhr.send();
      xhr.onload = function () {
        if (xhr.status === 200) {
          const newFile = new File([xhr.response], 'test.pdf', {
            type: 'application/pdf',
          });
          return res(newFile);
        }
        return rej(new Error());
      };
    });
  }

  static async loadDocumentEditorContentVersions(
    documentId: string
  ): Promise<EditorContentVersionMapping[]> {
    const {
      data: { latestContentVersions },
    } = await apolloClientInternal.query({
      query: documentEditorContentVersionsQuery,
      variables: { documentId },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return latestContentVersions;
  }

  static async loadLinkPreview(targetUrl: string): Promise<Partial<ContentLinkBlock>> {
    const {
      data: { previewContentLinkBlock },
    } = await apolloClientInternal.query({
      query: previewLinkQuery,
      variables: { targetUrl },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return previewContentLinkBlock;
  }
}
